<template>
  <div
    class="rainput"
    :class="`rainput_${direction}`"
  >
    <slot
      name="before"
      :methods="{ handleInput }"
      :values="l_values"
    >
    </slot>
    <div
      ref="cont"
      class="rainput_content"
      :class="[{ 'no-events cursor-default': readOnly }]"
      draggable="false"
      @mouseup="handleMouseup"
    >
      <div
        class="rainput_inputs"
        :class="`rainput_inputs_${direction}`"
      >
        <div
          v-for="(item, idx) in l_values"
          :key="`indi_${idx}_${item.id}`"
          class="rainput_inputs_item"
          :style="`--input-percentage: ${item.percentage * 100}%;
        --input-left-percentage: ${getInputLeft(idx) * 100}%;
        ${item.cssStyle};`"
          :class="item.cssClass"
          :data-tst="`handle_${idx}`"
          @mousedown.stop="e => handleMousedown(e, idx)"
          @touchstart.stop="e => handleMousedown(e, idx)"
          @wheel.stop="e => handleWheel(e, idx)"
        >
          <div
            v-show="!readOnly"
            class="rainput_inputs_item_handle"
          ></div>
        </div>
      </div>
    </div>
    <slot
      name="after"
      :methods="{ handleInput }"
      :values="l_values"
    >
    </slot>
  </div>
</template>

<script>
import { throttle } from 'lodash'
import format_mix from '@/mixins/format_mix'

export default {
  name: 'RangeInput',
  mixins: [format_mix],
  props: {
    readOnly: Boolean,
    min: { default: 0 },
    max: { default: 1 },
    balance: { default: 1 },
    direction: { default: 'horizontal' }, // vertical
    values: {
      default: () => [
        {
          id: '',
          cssStyle: '',
          cssClass: '',
          percentage: 0,
        },
      ],
    },
  },
  emits: ['update:wheelend', 'values:update', 'values:updateend'],
  data() {
    return {
      activeInputIdx: -1,
      mousedown: false,
      l_values: [],

      wheelEndTimeout: null,
    }
  },
  methods: {
    removeEventListeners() {
      window.removeEventListener('mousemove', this.handleMousemove)
      window.removeEventListener('touchmove', this.handleMousemove)
      window.removeEventListener('mouseup', this.handleMouseup)
      window.removeEventListener('touchend', this.handleMouseup)
    },
    addEventListeners() {
      window.addEventListener('mousemove', this.handleMousemove)
      window.addEventListener('touchmove', this.handleMousemove)
      window.addEventListener('mouseup', this.handleMouseup)
      window.addEventListener('touchend', this.handleMouseup)
    },
    handleMouseup() {
      this.mousedown = false
      this.activeInputIdx = -1
      this.removeEventListeners()
      this.emitUpdateEnd()
    },
    handleMousedown(e, idx) {
      e.preventDefault()
      this.mousedown = true
      this.setActiveInput(idx)
      this.addEventListeners()
      this.handleMousemove(e)
    },
    emitWheelEnd(throttleTime = 5) {
      clearTimeout(this.wheelEndTimeout)
      this.wheelEndTimeout = setTimeout(() => {
        this.$emit('update:wheelend')
      }, throttleTime + 20)
    },
    setActiveInput(idx) {
      this.activeInputIdx = idx === this.values.length - 1 ? this.values.length - 2 : idx
    },
    handleWheel(e, idx, max = 1, min = 0) {
      e.preventDefault()
      this.throttleWheel(e, idx, max, min)
    },
    throttleWheel: throttle(function (e, idx, max = 1, min = 0) {
      const values = [...this.values]
      idx = idx === values.length - 1 ? idx - 1 : idx
      let val = values[idx].percentage
      if (e.deltaY < 0) {
        val += 0.01
      } else {
        val -= 0.01
      }
      this.handleInput(val, idx, true)
      this.emitWheelEnd()
    }, 5),
    handleMousemove(e) {
      e.preventDefault()
      this.throttleMouseMove(e)
    },
    throttleMouseMove: throttle(function (e) {
      let { clientX, clientY } = e
      if (e?.touches) {
        clientX = e?.touches[0]?.clientX
        clientY = e?.touches[0]?.clientY
      }
      const { width, left, right, height, top, bottom } = this.$refs.cont.getBoundingClientRect()
      let percent = 0
      if (this.direction === 'horizontal') {
        percent = clientX <= left ? 0 : clientX >= right ? 1 : (clientX - left) / width
      } else {
        percent = clientY <= top ? 0 : clientY >= bottom ? 1 : (clientY - top) / height
        percent = 1 - percent
      }
      this.handleInput(percent, this.activeInputIdx)
    }, 1),
    balanceValues(items, aIdx, min = this.min, max = this.max, total = this.balance) {
      items = items.map(i => {
        // https://stackoverflow.com/questions/21690070/javascript-float-comparison
        let num = Number(i.percentage.toFixed(10))
        num = num > max ? max : num < min ? min : num
        // console.log(num)
        return {
          ...i,
          percentage: num,
        }
      })

      const nextIdx = aIdx + 1 > items.length - 1 ? 0 : aIdx + 1
      let l_total = items.reduce((acc, i) => acc + i.percentage, 0)
      const balance = total - l_total
      const res = items.map((i, idx) => {
        if (idx === aIdx) {
          return i
        }
        if (idx === nextIdx) {
          const num = i.percentage + balance

          if (num <= max && num >= min) {
            i.percentage = num
          }
          if (num < min) {
            i.percentage = 0
          }
          return i
        }
        return i
      })
      l_total = res.reduce((acc, i) => acc + i.percentage, 0)
      if (l_total !== total && this.min !== this.max) {
        return this.balanceValues(res, nextIdx)
      }

      // res = res.map(i => {
      //   // https://stackoverflow.com/questions/21690070/javascript-float-comparison
      //   let num = Number(i.percentage.toFixed(10))
      //   num = num > max ? max : num < min ? min : num
      //   return {
      //     ...i,
      //     percentage: num,
      //   }
      // })

      return res
    },
    decreasePrev(values, offsetVal, index) {
      // values = values.map(i => i)
      let prevIndex = index - 1
      prevIndex = prevIndex < 0 ? 0 : prevIndex

      const prevVal = values[prevIndex].percentage + offsetVal
      values[prevIndex].percentage = prevVal < 0 ? 0 : prevVal

      if (prevVal <= 0 && prevIndex > 0) {
        return this.decreasePrev(values, offsetVal, prevIndex)
      }
      return values
    },
    getInputLeft(idx) {
      let res = 0
      for (let i = 0; i < idx; i++) {
        res += this.l_values[i].percentage
      }
      return res
    },
    handleInput(value, index, manual = false) {
      if (this.readOnly) return

      // if (this.decimals) {
      //   value = this.numFixed(value, this.decimals)
      // }

      let values = [...this.values]
      if (!manual) {
        let offsetVal = 0
        if (index === this.values.length - 1) {
          return
        }
        const offsetLeft = this.getInputLeft(index)
        offsetVal = value - offsetLeft
        if (offsetVal < 0) {
          values = this.decreasePrev(values, offsetVal, index + 1)
        } else {
          values[index].percentage = offsetVal
        }
      } else {
        values[index].percentage = value
      }

      values = this.balance ? this.balanceValues(values, index) : values
      this.l_values = values
      // console.log(values.map(i => i.percentage))
      // console.log(values.reduce((a, b) => a + b.percentage, 0))
      // console.log(this.balance)
      // console.log('values', values)
      this.emitUpdate(values)
    },
    emitUpdate: throttle(function (values) {
      this.$emit('values:update', {
        values,
      })
    }, 44),
    emitUpdateEnd: throttle(function () {
      this.$emit('values:updateend', {
        values: this.l_values,
      })
    }, 44),
    setValues() {
      this.l_values = this.values
    },
  },

  watch: {
    values: {
      immediate: true,
      handler: 'setValues',
    },
  },
}
</script>
<style lang="scss" scoped>
.rainput {
  --rainput-clr-back: var(--clr-black-3);
  --rainput-height: 1rem;

  --rainput-handle-width: var(--rainput-height);
  --rainput-handle-height: var(--rainput-height);

  --handle-transform: translate(50%, 0%);
  --handle-border: solid var(--clr-white-0);
  --handle-border-radius: 4px;

  &_variant_circle {
    --rainput-height: 0.5rem;
    --rainput-handle-width: calc(var(--rainput-height) * 1.5);
    --rainput-handle-height: calc(var(--rainput-height) * 1.5);
    --handle-transform: translate(50%, -15%);
    --handle-border: solid var(--clr-black-1);
    --handle-border-radius: calc(var(--rainput-handle-width) / 2);
  }

  position: relative;
  width: 100%;
  display: flex;
  &_horizontal {
    flex-direction: row;
    .rainput_content {
      width: 100%;
      height: var(--rainput-height);
    }
  }
  &_vertical {
    flex-direction: column-reverse;
    .rainput_content {
      width: var(--rainput-height);
      height: 100%;
    }
  }
  &_content {
    display: flex;
    background: var(--rainput-clr-back);
    border-radius: 4px;
    cursor: pointer;
  }
  &_inputs {
    position: relative;

    --input-percentage: 0%;
    --input-left-percentage: 0%;
    --clr-input-back: currentColor;
    --clr-input-handle: currentColor;

    &_horizontal {
      height: var(--rainput-height);
      width: calc(100% - var(--rainput-handle-width));
      margin-left: calc(var(--rainput-handle-width) / 2);
      .rainput_inputs_item {
        left: var(--input-left-percentage);
        width: var(--input-percentage);
        height: 100%;

        &:first-child {
          &::before {
            top: 0;
            left: calc(-1 * var(--rainput-handle-width) / 2);
            width: var(--rainput-handle-width);
            height: 100%;
            border-radius: 4px 0 0 4px;
          }
        }
        &:last-child {
          .rainput_inputs_item_handle {
            display: none;
          }
          &::before {
            top: 0;
            right: calc(-1 * var(--rainput-handle-width) / 2);
            width: var(--rainput-handle-width);
            height: 100%;
            border-radius: 0 4px 4px 0;
          }
        }
        &_handle {
          top: 0;
          right: 0;
          width: var(--rainput-handle-width);
          height: var(--rainput-handle-height);
          transform: var(--handle-transform);
        }
      }
    }
    &_vertical {
      height: calc(100% - var(--rainput-handle-height));
      width: var(--rainput-height);
      margin-top: calc(var(--rainput-handle-height) / 2);
      .rainput_inputs_item {
        bottom: var(--input-left-percentage);
        height: var(--input-percentage);
        width: 100%;
        &:first-child {
          &::before {
            bottom: calc(-1 * var(--rainput-handle-height) / 2);
            left: 0;
            height: var(--rainput-handle-height);
            width: 100%;
            border-radius: 0 0 4px 4px;
          }
        }
        &:last-child {
          .rainput_inputs_item_handle {
            display: none;
          }
          &::before {
            top: calc(-1 * var(--rainput-handle-height) / 2);
            left: 0;
            height: var(--rainput-handle-height);
            width: 100%;
            border-radius: 4px 4px 0 0;
          }
        }
        &_handle {
          top: 0;
          left: 0;
          width: var(--rainput-handle-width);
          height: var(--rainput-handle-height);
          transform: translate(0%, -50%);
        }
      }
    }
    &_item {
      position: absolute;
      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        background: var(--clr-input-back);
        height: 100%;
        width: 100%;
        z-index: 1;
      }
      &:first-child {
        &::before {
          content: '';
          position: absolute;
          background: var(--clr-input-back);
        }
      }
      &:last-child {
        .rainput_inputs_item_handle {
          display: none;
        }
        &::before {
          content: '';
          position: absolute;
          background: inherit;
        }
      }
      &_handle {
        position: absolute;
        z-index: 4;
        border-radius: var(--handle-border-radius);
        background: var(--clr-input-handle);
        border: var(--handle-border);
        transition: 0.22s;
        &:hover,
        &:active {
          box-shadow: 0px 0px 0px 2px var(--clr-white-0);
        }
      }
    }
  }
}
</style>
